import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Crown, ArrowRight } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ComparisonPage = () => {
  const { slug } = useParams();
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/piece-comparisons/${slug}`);
        if (!cancelled) setComp(res.data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !comp) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Comparison Not Found</h1>
        <Link to="/" className="btn-editorial">Back to Home</Link>
      </div>
    );
  }

  // Use the explicit winner_index from the backend (set per-comparison in comparisons_data.py).
  // Falls back to a string-prefix match if the field is missing.
  const winnerIndex =
    typeof comp.winner_index === 'number'
      ? comp.winner_index
      : comp.pieces.findIndex((p) =>
          p
            .toLowerCase()
            .startsWith((comp.winner || '').toLowerCase().split(' ')[0])
        );

  // FAQ schema seeded from the table — first 3 rows become Q&As
  const faqs = comp.rows.slice(0, 3).map((row) => ({
    question: `What ${row.label.toLowerCase()} should I expect for ${comp.pieces.join(', ')}?`,
    answer: `${comp.pieces.map((p, i) => `${p}: ${row.values[i]}`).join('. ')}.`,
  }));

  return (
    <div data-testid={`comparison-page-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={`${comp.title} · Jewelry Refined`}
        description={comp.intro.slice(0, 158)}
        url={`/vs/${slug}`}
        type="article"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{ items: [
          { name: 'Home', url: '/' },
          { name: 'Comparisons', url: '/vs' },
          { name: comp.title.split(' — ')[0], url: `/vs/${slug}` },
        ]}}
      />
      <SchemaOrg type="faqpage" data={{ items: faqs }} />

      {/* Editorial Hero */}
      <section className="bg-[#111] text-white">
        <div className="container-editorial pt-20 pb-16 lg:pt-24">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <Link to={`/clusters/${comp.cluster_slug}`} className="hover:text-[#C69C6D] transition-colors">
              {comp.category}
            </Link>
            <span>/</span>
            <span className="text-white">Head-to-Head</span>
          </nav>
          <p className="overline text-[#C69C6D] mb-6">Head-to-Head Comparison</p>
          <h1 className="font-heading font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
            {comp.title}
          </h1>
          <p className="font-heading italic text-lg lg:text-xl text-white/85 max-w-3xl">{comp.intro}</p>
        </div>
      </section>

      {/* The Table */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full min-w-[700px] border-collapse" data-testid="comparison-table">
              <thead>
                <tr className="border-b-2 border-[#111]">
                  <th className="text-left py-5 pr-6 text-[10px] tracking-[0.2em] uppercase text-[#888] font-normal align-bottom w-1/4">
                    Specification
                  </th>
                  {comp.pieces.map((piece, i) => (
                    <th
                      key={i}
                      className={`text-left py-5 px-6 align-bottom ${i === winnerIndex ? 'bg-[#FAF6EE]' : ''}`}
                    >
                      {i === winnerIndex && (
                        <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#C69C6D] mb-2">
                          <Crown className="w-3 h-3" strokeWidth={1.75} /> Editor&apos;s Pick
                        </span>
                      )}
                      <p className="font-heading text-xl lg:text-2xl text-[#111] font-light leading-tight">{piece}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comp.rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-[#E5E0D8]">
                    <td className="py-4 pr-6 text-xs tracking-[0.1em] uppercase text-[#555] font-medium">
                      {row.label}
                    </td>
                    {row.values.map((v, ci) => (
                      <td
                        key={ci}
                        className={`py-4 px-6 text-base text-[#111] font-light ${ci === winnerIndex ? 'bg-[#FAF6EE] font-medium' : ''}`}
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Winner Thesis */}
      <section className="section-editorial bg-[#111] text-white">
        <div className="container-editorial max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Crown className="w-5 h-5 text-[#C69C6D]" strokeWidth={1.5} />
            <p className="overline text-[#C69C6D]">Editor's Verdict</p>
          </div>
          <h2 className="font-heading text-3xl lg:text-5xl font-light leading-tight mb-6">
            The Winner: <span className="italic text-[#C69C6D]">{comp.winner}</span>
          </h2>
          <p className="text-xl lg:text-2xl text-white/85 font-light leading-relaxed font-heading italic">
            "{comp.winner_thesis}"
          </p>
        </div>
      </section>

      {/* CTAs */}
      <section className="py-16 lg:py-20">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link
              to="/drops"
              className="group block p-8 border border-[#E5E0D8] hover:border-[#111] transition-all bg-white"
            >
              <p className="overline overline-gold mb-3">Shop Now</p>
              <p className="font-heading text-2xl text-[#111] group-hover:text-[#C69C6D] mb-4 transition-colors">
                See the latest drops on {comp.winner}
              </p>
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase">
                Browse The Drop Tracker
                <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </span>
            </Link>
            <Link
              to={`/clusters/${comp.cluster_slug}`}
              className="group block p-8 border border-[#E5E0D8] hover:border-[#111] transition-all bg-white"
            >
              <p className="overline overline-gold mb-3">Read More</p>
              <p className="font-heading text-2xl text-[#111] group-hover:text-[#C69C6D] mb-4 transition-colors">
                The {comp.category} archive
              </p>
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase">
                Open the Cluster Hub
                <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparisonPage;
